import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { BtnCloseModal } from '../../../../assets';
import { testProps } from '../../../../config';
import { clearBanNotice } from '../../../../store/auth/authActions';
import { selectBanNotice } from '../../../../store/auth/authReducer';
import { useDevice } from '../../../../utils/hooks/useDevice';
import { TouchableOpacity } from '../../common/Touchable';
import SvaraModal from '../SvaraModal';

const formatExpiry = (iso: string) => {
	const date = new Date(iso);

	if (Number.isNaN(date.getTime())) {
		return iso;
	}

	return date.toLocaleString();
};

/**
 * Global "account suspended" screen. Opens when gateway-game refuses a login /
 * registration with one of the T_AUTH_LOGIN_*_BANNED codes, or when the game socket
 * receives `auth:session-revoked` with reason `banned` while the player is in the app.
 * Mounted once in RootNavigator so it is reachable from any screen.
 */
function BannedModal() {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const banNotice = useSelector(selectBanNotice);
	const { isMobile, isPortrait } = useDevice();
	const isMobilePortrait = isMobile && isPortrait;

	if (!banNotice) {
		return null;
	}

	const handleClose = () => dispatch(clearBanNotice());
	const isPermanent = !banNotice.expiresAt;

	return (
		<SvaraModal
			visible
			onClose={handleClose}
			closeIcon={BtnCloseModal}
			bigCloseButton
			bigCloseBtnStyles={styles.closeBtn}
			contentWrapperStyles={styles.contentWrapper}
			containerStyles={isMobilePortrait ? { width: '85%' } : undefined}
		>
			<Text style={styles.title} {...testProps('banned-title')}>
				{t('T_SITE_ACCOUNT_SUSPENDED_TITLE')}
			</Text>
			<Text style={styles.message} {...testProps('banned-message')}>
				{banNotice.message}
			</Text>
			<View style={styles.details}>
				<Text style={styles.detail}>
					{isPermanent
						? t('T_SITE_ACCOUNT_SUSPENDED_PERMANENT')
						: t('T_SITE_ACCOUNT_SUSPENDED_UNTIL', {
								date: formatExpiry(banNotice.expiresAt as string),
							})}
				</Text>
				{banNotice.reason ? (
					<Text style={styles.detail}>
						{t('T_SITE_ACCOUNT_SUSPENDED_REASON', { reason: banNotice.reason })}
					</Text>
				) : null}
			</View>
			<TouchableOpacity onPress={handleClose} style={styles.okBtn} {...testProps('cta-banned-ok')}>
				<Text style={styles.okText}>{t('T_OK') || 'OK'}</Text>
			</TouchableOpacity>
		</SvaraModal>
	);
}

const styles = StyleSheet.create({
	closeBtn: {
		top: -20,
		right: -10,
	},
	contentWrapper: {
		paddingBottom: 12,
		gap: 14,
	},
	title: {
		color: '#ff6b6b',
		fontSize: 20,
		fontWeight: '700',
		textAlign: 'center',
		paddingHorizontal: 16,
	},
	message: {
		color: 'white',
		fontSize: 16,
		lineHeight: 22,
		textAlign: 'center',
		paddingHorizontal: 16,
	},
	details: {
		gap: 6,
		alignItems: 'center',
		paddingHorizontal: 16,
	},
	detail: {
		color: '#d7e6ff',
		fontSize: 14,
		textAlign: 'center',
	},
	okBtn: {
		minWidth: 140,
		alignSelf: 'center',
		paddingVertical: 12,
		paddingHorizontal: 18,
		borderRadius: 12,
		borderWidth: 1,
		borderColor: '#4CAF50',
		backgroundColor: '#4CAF50',
		alignItems: 'center',
		justifyContent: 'center',
		marginTop: 4,
	},
	okText: {
		color: '#ffffff',
		fontSize: 16,
		fontWeight: '700',
	},
});

export default BannedModal;
